import { useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { format, parse } from 'date-fns';
import Skeleton from '../../../components/layout/Skeleton';
import Descricao from '../../../components/tipografia/Descricao';
import Icon from '@/components/Icon';
import { mdiMenuUp, mdiMenuDown } from '@mdi/js';
import { translateFilter, corPorScore, corFundoPastel, periodoAnterior } from '../util';
import {
  mesclarLinhas,
  ordenarLinhas,
  anexarDelta,
  formatTempo,
  formatDeltaPontos,
  formatDeltaPercentual,
  formatDeltaTempo,
  Coluna,
  Direcao,
  LinhaMensagem,
  LinhaScore,
} from './mesclarPerformanceAtendentes';

type Props = {
  dataInicio: string;
  dataFim: string;
  grupoId: number[];
};

/** Busca as duas fontes do período e já devolve uma linha por atendente. */
const buscarPeriodo = async (
  dataInicio: string,
  dataFim: string,
  grupoId: number[],
) => {
  const filtro = translateFilter({ periodo: ['custom', dataInicio, dataFim], grupo_id: grupoId });

  const qsMensagens = new URLSearchParams();
  qsMensagens.append('facts', 'qtd_chats,avg_analise_sentimento_cliente,avg_analise_comportamento_atendente,avg_tempo_resposta_segundos');
  qsMensagens.append('dimensions', 'usuario_responsavel_id,usuario_responsavel_nome');
  qsMensagens.append('filter', filtro);

  const qsScore = new URLSearchParams();
  qsScore.append('facts', 'avg_score');
  qsScore.append('dimensions', 'usuario_responsavel_id,usuario_responsavel_nome');
  // NPS é a pesquisa padrão; outras pesquisas (chaves customizadas) ficam de fora
  // pra não misturar naturezas diferentes de nota nesse ranking.
  qsScore.append('filter', JSON.stringify({ ...JSON.parse(filtro), chave: ['eq', 'nps'] }));

  const [mensagens, scores] = await Promise.all([
    axios.get('/api/relatorios/chat_mensagem?' + qsMensagens.toString()),
    axios.get('/api/relatorios/chat_pontuacao?' + qsScore.toString()),
  ]);

  return mesclarLinhas(
    mensagens.data.result as LinhaMensagem[],
    scores.data.result as LinhaScore[],
  );
};

const CabecalhoOrdenavel = ({
  coluna,
  sortBy,
  sortDir,
  onClick,
  children,
}: {
  coluna: Coluna;
  sortBy: Coluna;
  sortDir: Direcao;
  onClick: (coluna: Coluna) => void;
  children: React.ReactNode;
}) => (
  <th
    style={{ cursor: 'pointer', padding: '8px', textAlign: 'left', userSelect: 'none', whiteSpace: 'nowrap' }}
    onClick={() => onClick(coluna)}
  >
    {children}
    {sortBy === coluna && <Icon path={sortDir === 'asc' ? mdiMenuUp : mdiMenuDown} size={0.9} />}
  </th>
);

/**
 * Variação vs. período anterior. A seta segue o sinal (subiu/caiu) e a cor segue
 * se isso é bom — em tempo de resposta, cair é melhorar, daí `menorEhMelhor`.
 */
const Delta = ({
  valor,
  formatar,
  menorEhMelhor = false,
}: {
  valor: number | null | undefined;
  formatar: (v: number) => string;
  menorEhMelhor?: boolean;
}) => {
  // Abaixo de meio ponto/segundo a formatação arredondaria pra zero e a seta mentiria.
  if (valor == null || Math.abs(valor) < 0.5) return null;

  const subiu = valor > 0;
  const bom = menorEhMelhor ? !subiu : subiu;

  return (
    <span
      style={{
        fontSize: '0.75rem',
        fontWeight: 'normal',
        color: bom ? 'var(--tc-color-success)' : 'var(--tc-color-danger)',
      }}
    >
      {subiu ? '▲' : '▼'} {formatar(Math.abs(valor))}
    </span>
  );
};

/**
 * Célula colorida pela faixa de qualidade de `cor` (null = sem dado, sem cor).
 * Só ganha destaque (fundo + texto colorido) abaixo de 50 — "Pode melhorar"/"Regular".
 * Score bom/excelente fica neutro: numa tabela com várias colunas, colorir tudo
 * que já está ok some com o sinal de onde realmente vale olhar.
 */
const CelulaColorida = ({
  cor,
  texto,
  delta,
  formatarDelta = formatDeltaPontos,
  menorEhMelhor = false,
}: {
  cor: number | null;
  texto: string;
  delta: number | null | undefined;
  formatarDelta?: (v: number) => string;
  menorEhMelhor?: boolean;
}) => {
  if (cor == null) {
    return <td style={{ padding: '8px', textAlign: 'center', color: 'var(--tc-color-gray-500)' }}>—</td>;
  }

  const precisaAtencao = cor < 50;

  return (
    <td
      style={{
        padding: '8px',
        textAlign: 'center',
        backgroundColor: precisaAtencao ? corFundoPastel(cor) : undefined,
        color: precisaAtencao ? corPorScore(cor) : undefined,
        fontWeight: 'bold',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '4px' }}>
        {texto}
        <Delta valor={delta} formatar={formatarDelta} menorEhMelhor={menorEhMelhor} />
      </span>
    </td>
  );
};

const formatDia = (data: string) => format(parse(data, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy');

const TabelaPerformanceAtendentes = ({ dataInicio, dataFim, grupoId }: Props) => {
  const anterior = periodoAnterior(dataInicio, dataFim);

  const qAtual = useQuery(
    ['performanceAtendentes', 'atual', dataInicio, dataFim, grupoId],
    () => buscarPeriodo(dataInicio, dataFim, grupoId),
    { refetchOnWindowFocus: false },
  );

  const qAnterior = useQuery(
    ['performanceAtendentes', 'anterior', anterior.dataInicio, anterior.dataFim, grupoId],
    () => buscarPeriodo(anterior.dataInicio, anterior.dataFim, grupoId),
    { refetchOnWindowFocus: false },
  );

  const [sortBy, setSortBy] = useState<Coluna>('nps');
  const [sortDir, setSortDir] = useState<Direcao>('desc');

  const toggleSort = (coluna: Coluna) => {
    if (sortBy === coluna) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(coluna);
      setSortDir('desc');
    }
  };

  if (qAtual.isFetching || qAnterior.isFetching) {
    return <Skeleton format="box" />;
  }

  const linhas = ordenarLinhas(
    anexarDelta(qAtual.data ?? [], qAnterior.data ?? []),
    sortBy,
    sortDir,
  );

  if (linhas.length === 0) {
    return <p>Nenhum atendente encontrado no período.</p>;
  }

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <Descricao style={{ marginBottom: '8px' }}>
        Variação comparada com {formatDia(anterior.dataInicio)} a {formatDia(anterior.dataFim)}.
      </Descricao>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--tc-color-gray-200)' }}>
            <CabecalhoOrdenavel coluna="nome" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}>Atendente</CabecalhoOrdenavel>
            <CabecalhoOrdenavel coluna="qtdChats" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}>Qtd. atendimentos</CabecalhoOrdenavel>
            <CabecalhoOrdenavel coluna="nps" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}>NPS</CabecalhoOrdenavel>
            <CabecalhoOrdenavel coluna="sentimento" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}>Sentimento do cliente</CabecalhoOrdenavel>
            <CabecalhoOrdenavel coluna="comportamento" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}>Comportamento do atendente</CabecalhoOrdenavel>
            <CabecalhoOrdenavel coluna="tempoRespostaScore" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}>Tempo médio de resposta</CabecalhoOrdenavel>
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha) => (
            <tr key={linha.id} style={{ borderBottom: '1px solid var(--tc-color-gray-200)' }}>
              <td style={{ padding: '8px', fontWeight: 'bold' }}>{linha.nome}</td>
              <td style={{ padding: '8px', textAlign: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '4px' }}>
                  {linha.qtdChats}
                  <Delta valor={linha.delta?.qtdChatsPercentual} formatar={formatDeltaPercentual} />
                </span>
              </td>
              <CelulaColorida cor={linha.nps} texto={linha.nps != null ? `${Math.round(linha.nps)}%` : ''} delta={linha.delta?.nps} />
              <CelulaColorida cor={linha.sentimento} texto={linha.sentimento != null ? `${Math.round(linha.sentimento)}%` : ''} delta={linha.delta?.sentimento} />
              <CelulaColorida cor={linha.comportamento} texto={linha.comportamento != null ? `${Math.round(linha.comportamento)}%` : ''} delta={linha.delta?.comportamento} />
              <CelulaColorida
                cor={linha.tempoRespostaScore}
                texto={linha.tempoRespostaSegundos != null ? formatTempo(linha.tempoRespostaSegundos) : ''}
                delta={linha.delta?.tempoRespostaSegundos}
                formatarDelta={formatDeltaTempo}
                menorEhMelhor
              />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TabelaPerformanceAtendentes;
